import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Alert, Box, IconButton, Snackbar, Stack} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import CommentEditor from './comments/editor/CommentEditor';
import {
    buildCommentDocFromPlainText,
    extractPlainTextFromCommentDoc,
} from '../features/aiAssistantChat/utils/commentComposerUtils';
import {createComment} from '../utils/api/comments';

const BASE = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';

async function uploadChatImage(chatId, file) {
    const token = localStorage.getItem('access_token');
    const form = new FormData();
    form.append('file', file);
    const resp = await fetch(`${BASE}/api/chats/${encodeURIComponent(chatId)}/upload`, {
        method: 'POST',
        headers: token ? {Authorization: `Bearer ${token}`} : {},
        body: form,
    });
    if (!resp.ok) throw new Error(`Image upload failed: ${resp.status}`);
    return resp.json(); // { url }
}

/**
 * MessageComposer
 *
 * Single unified message input built on CommentEditor (ProseMirror).
 * Used for AI assistant chat, human 1-on-1 chat, and document annotations.
 *
 * Props:
 *   onSubmit(text)          — required for AI/policymaker; called with plain-text string
 *   onSubmitRich(json)      — optional; called with raw ProseMirror JSON instead (Yjs annotations)
 *   placeholder             — string (default: "Type a message…")
 *   submitLabel             — string (default: "Send")
 *   disabled                — boolean
 *   submitDisabled          — boolean
 *   autoFocus               — boolean
 *   fullHeight              — boolean; adds top border
 *   // controlled text (AI assistant)
 *   value                   — string
 *   onChange(text)          — called on every change
 *   onFocusChange(focused)  — called on focus/blur
 *   inputRef                — ref forwarded to CommentEditor imperative handle
 *   ariaLabel               — accessible name for the textbox (default: "Message input")
 *   // edit mode (annotations)
 *   initialContent          — ProseMirror JSON doc; pre-populates editor
 *   onCancel()              — shows Cancel button when provided
 *   // human 1-on-1 chat (clipboard image paste + REST persistence)
 *   elementName             — string; e.g. "OneOnOne" — required when chatId is set
 *   chatId                  — string; if provided, enables image upload and REST persistence
 *   userId                  — string; sender uid (included in persisted message)
 *   userDisplayName         — string; sender display name
 *   onOptimisticMessage(p)  — called immediately before async send with message payload
 */
const MessageComposer = ({
    onSubmit,
    onSubmitRich,
    placeholder = 'Type a message…',
    submitLabel = 'Send',
    disabled = false,
    submitDisabled = false,
    autoFocus = false,
    fullHeight = false,
    value,
    onChange,
    onFocusChange,
    inputRef,
    initialContent,
    onCancel,
    elementName,
    chatId,
    userId,
    userDisplayName,
    onOptimisticMessage,
    ariaLabel = 'Message input',
}) => {
    const editorRef = useRef(null);
    const syncedTextRef = useRef(value || '');

    // Image paste state (only active when chatId is provided)
    const [pastedImage, setPastedImage] = useState(null); // { file, previewUrl }
    const [sending, setSending] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);

    // Cleanup blob URL on unmount or image change
    useEffect(() => () => {
        if (pastedImage?.previewUrl) URL.revokeObjectURL(pastedImage.previewUrl);
    }, [pastedImage]);

    // Forward inputRef to the CommentEditor imperative handle
    const setEditorInstance = useCallback((instance) => {
        editorRef.current = instance;
        if (typeof inputRef === 'function') {
            inputRef(instance);
        } else if (inputRef && typeof inputRef === 'object') {
            inputRef.current = instance;
        }
    }, [inputRef]);

    // Sync controlled `value` prop into editor (AI assistant use case)
    useEffect(() => {
        const next = value || '';
        if (syncedTextRef.current === next) return;
        editorRef.current?.setContent(buildCommentDocFromPlainText(next));
        syncedTextRef.current = next;
    }, [value]);

    const handleEditorChange = useCallback((json, meta = {}) => {
        if (meta.docChanged === false) return;
        const text = extractPlainTextFromCommentDoc(json);
        syncedTextRef.current = text;
        onChange?.(text);
    }, [onChange]);

    const clearImage = useCallback(() => {
        if (pastedImage?.previewUrl) URL.revokeObjectURL(pastedImage.previewUrl);
        setPastedImage(null);
    }, [pastedImage]);

    // Attach paste listener to the wrapper div to intercept clipboard images.
    // ponytail: CommentEditor doesn't expose a paste hook; we attach to its DOM wrapper.
    const wrapperRef = useRef(null);
    useEffect(() => {
        if (!chatId || !wrapperRef.current) return;
        const el = wrapperRef.current;
        const handler = (e) => {
            const items = e.clipboardData?.items;
            if (!items) return;
            for (const item of items) {
                if (item.type.startsWith('image/')) {
                    const file = item.getAsFile();
                    if (!file) continue;
                    if (pastedImage?.previewUrl) URL.revokeObjectURL(pastedImage.previewUrl);
                    setPastedImage({file, previewUrl: URL.createObjectURL(file)});
                    e.preventDefault();
                    return;
                }
            }
        };
        el.addEventListener('paste', handler);
        return () => el.removeEventListener('paste', handler);
    }, [chatId, pastedImage]);

    const handleSubmit = useCallback(async (json) => {
        if (disabled || submitDisabled) return;

        // Annotation path: pass raw ProseMirror JSON to Yjs caller
        if (onSubmitRich) {
            onSubmitRich(json);
            editorRef.current?.clear();
            return;
        }

        const text = extractPlainTextFromCommentDoc(json).trim();
        if (!text && !pastedImage) return;

        // Human chat path: upload image if any, optimistically show, then persist via REST
        if (chatId) {
            setSending(true);
            try {
                let imageUrl = null;
                if (pastedImage?.file) {
                    const {url} = await uploadChatImage(chatId, pastedImage.file);
                    imageUrl = url;
                }
                const msgPayload = {
                    content: text,
                    userId,
                    userDisplayName,
                    publishedDatetime: Date.now(),
                    ...(imageUrl ? {imageUrl} : {}),
                };
                onOptimisticMessage?.(msgPayload);
                clearImage();
                syncedTextRef.current = '';
                await createComment(elementName, chatId, msgPayload);
                setTimeout(() => editorRef.current?.focus(), 0);
            } catch (e) {
                console.error('Failed to send message:', e);
                setErrorMsg(e?.message || 'Failed to send message');
            } finally {
                setSending(false);
            }
            return;
        }

        // Plain text path (AI assistant, policymaker)
        syncedTextRef.current = text;
        onSubmit?.(text);
    }, [disabled, submitDisabled, onSubmitRich, pastedImage, chatId, elementName, userId, userDisplayName, onOptimisticMessage, clearImage, onSubmit]);

    return (
        <Stack
            ref={wrapperRef}
            px={chatId ? 1 : 2}
            py={chatId ? '6px' : 1.5}
            borderTop={fullHeight ? '1px solid #eee' : 1}
            borderColor="divider"
            bgcolor="background.paper"
            flexShrink={0}
            spacing={0.75}
        >
            <Snackbar
                open={!!errorMsg}
                autoHideDuration={5000}
                onClose={() => setErrorMsg(null)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            >
                <Alert severity="error" onClose={() => setErrorMsg(null)} sx={{width: '100%'}}>
                    {errorMsg}
                </Alert>
            </Snackbar>

            {/* Image preview strip — only when chatId is set and an image was pasted */}
            {pastedImage && (
                <Box sx={{position: 'relative', display: 'inline-flex', alignSelf: 'flex-start'}}>
                    <Box
                        component="img"
                        src={pastedImage.previewUrl}
                        alt="pasted"
                        sx={{
                            maxHeight: 120,
                            maxWidth: '100%',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            display: 'block',
                        }}
                    />
                    <IconButton
                        size="small"
                        onClick={clearImage}
                        sx={{
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                            p: '2px',
                            '&:hover': {bgcolor: 'error.light', color: 'white'},
                        }}
                    >
                        <CloseIcon sx={{fontSize: 14}}/>
                    </IconButton>
                </Box>
            )}

            {sending
                ? <Box display="flex" justifyContent="center" py={1}><CircularProgress size={20}/></Box>
                : (
                    <CommentEditor
                        ref={setEditorInstance}
                        onSubmit={handleSubmit}
                        onCancel={onCancel}
                        onChange={handleEditorChange}
                        onFocusChange={onFocusChange}
                        initialContent={initialContent}
                        placeholder={placeholder}
                        submitLabel={submitLabel}
                        disabled={disabled}
                        submitDisabled={submitDisabled}
                        autoFocus={autoFocus}
                        submitOnEnter
                        keepFocusOnSubmit
                        ariaLabel={ariaLabel}
                    />
                )
            }
        </Stack>
    );
};

export default MessageComposer;
